import { z } from 'zod';

/**
 * Canonical ticker: upper-case alphanumerics with optional single-dot class
 * share (BRK.B, BF.B). Dot is canonical (plan §6.2 · naming §3.6).
 * `.max()` bounds the ACCEPTED value; it is not a regex guard — Zod runs every
 * string check and collects all issues, so `{ abort: true }` is what actually
 * stops TICKER_PATTERN executing against unbounded input.
 */
export const TICKER_MAX_LENGTH = 16;
export const TICKER_PATTERN = /^[A-Z][A-Z0-9]*(?:\.[A-Z0-9]+)?$/;

/** Printable ASCII only — see `normalizeRequestedTicker` for why. */
const ASCII_PRINTABLE = /^[\x20-\x7E]+$/;

export const TickerSchema = z
  .string()
  .max(TICKER_MAX_LENGTH, { abort: true })
  .regex(TICKER_PATTERN)
  .brand<'Ticker'>();
export type Ticker = z.infer<typeof TickerSchema>;

export function isTicker(value: unknown): value is Ticker {
  return TickerSchema.safeParse(value).success;
}

export function asTicker(value: string): Ticker {
  return TickerSchema.parse(value);
}

/**
 * Normalise a path/search alias to the requestedTicker wire shape:
 * upper-cased, hyphen→dot (class share), never the raw path string (plan §6.6 · data-flow §4.2).
 *
 * Non-ASCII is rejected BEFORE case-mapping: `toUpperCase()` is unicode-aware and
 * both folds into ASCII and expands length, so 'ıBM' would otherwise canonicalise
 * to 'IBM' and eight 'ß' would pass a 16-char guard as a 16-char result.
 *
 * EVERY rejection path throws `ZodError`. A caller writing the obvious
 * `catch (e) { if (e instanceof z.ZodError) → 400 else → 500 }` would otherwise
 * turn the length/empty/ASCII rejections into a 500 with a logged stack.
 */
const RawRequestedTickerSchema = z
  .string()
  .trim()
  .min(1)
  .max(TICKER_MAX_LENGTH)
  .regex(ASCII_PRINTABLE, { message: 'requestedTicker must be printable ASCII' });

export function normalizeRequestedTicker(raw: string): Ticker {
  const trimmed = RawRequestedTickerSchema.parse(raw);
  // Path-param normalizeTicker: brk-b / BRK-B / BRK.B → BRK.B
  const normalized = trimmed.toUpperCase().replace(/-/g, '.');
  return TickerSchema.parse(normalized);
}

/* Bounds the UNTRUSTED outer string before `.trim()` copies it. The `.max()` on
   TickerSchema applies to the normalized result, so the request-side schema
   inherits no bound of its own. Headroom over TICKER_MAX_LENGTH is deliberate —
   the ASCII guard rejects case-expanding input, this only stops amplification. */
export const RequestedTickerSchema = z
  .string()
  .max(TICKER_MAX_LENGTH * 4)
  .transform((raw, ctx) => {
    try {
      return normalizeRequestedTicker(raw);
    } catch {
      ctx.addIssue({
        code: 'custom',
        message: 'Invalid ticker',
      });
      return z.NEVER;
    }
  });

export const CentsSchema = z.number().int().nonnegative().brand<'Cents'>();
export type Cents = z.infer<typeof CentsSchema>;

/** Signed integer basis points (returns/drawdowns may be negative). */
export const BasisPointsSchema = z.number().int().brand<'BasisPoints'>();
export type BasisPoints = z.infer<typeof BasisPointsSchema>;
